import SubHeader from "../../components/header/SubHeader";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {DiscountType, Status} from "../../enums/enums";
import {camelToTitle} from "../../components/Utility";
import {useNavigate, useParams} from "react-router-dom";
import {fetchProductList} from "../../store/list-slice";
import {
    createDiscountToCustomer,
    fetchCustomerWithDiscountById,
    updateCustomerWithDiscount
} from "../../store/customer-discount-slice";
import Button from "../../components/common/button/Button";
import SelectOption from "../../components/common/form/SelectOption";

// other imports...

const DiscountAdd = () => {
    const params = useParams();
    const navigator = useNavigate();
    const dispatch = useDispatch();
    const [customerDiscountEdit, setCustomerDiscountEdit] = useState(null);
    const userRole = useSelector((state) => state.auth.selectedRole);
    const {items, error, createStatus, updateStatus} = useSelector(state => state.discount);

    const [productWithDiscount, setProductWithDiscount] = useState([{
        productId: "",
        productDiscountType: "",
        discountAmount: ""
    }]);

    const products = useSelector(state => state.list.product.items)

    useEffect(() => {
        dispatch(fetchProductList({...userRole, allProducts: true}))
    }, [dispatch, userRole])

    const handleAddRow = () => {
        setProductWithDiscount(prev => [...prev, {productId: "", productDiscountType: "", discountAmount: ""}]);
    };

    const handleRemoveRow = (index) => {
        setProductWithDiscount(prev => prev.filter((_, i) => i !== index));
    };


    const [formData, setFormData] = useState({
        id: userRole.scopeId,
        customer: params.customerId,
        isAllProducts: false,
        discountType: '',
        discount: '',
        maximumDiscount: '',
        products: [],
        startDate: "",
        endDate: "",
        status: 1
    });

    const [errors, setErrors] = useState({
        discountType: false,
        discount: false,
    });

    useEffect(() => {
        if (params.discountId) {
            dispatch(fetchCustomerWithDiscountById({...userRole, id: params.discountId}));
        }
    }, [dispatch, params.discountId, userRole]);

    useEffect(() => {
        if (params.discountId && items.length > 0) {
            const found = items.find(item => item.id === params.discountId);
            if (found) {
                setCustomerDiscountEdit(found);
                // Pre-fill form data
                setFormData(prevState => ({
                    ...prevState,
                    isAllProducts: found.isAllProducts,
                    discountType: found.discountType ? found.discountType : '',
                    discount: found.discount,
                    maximumDiscount: found.maximumDiscount,
                    startDate: found.startDate,
                    endDate: found.endDate,
                    status: found.status,
                }));

                if (!found.isAllProducts) {
                    setProductWithDiscount(found.products);
                }
            }
        }
    }, [params.discountId, items]);

    useEffect(() => {
        if (createStatus === "success" || updateStatus === "success") {
            navigator('/discount');
        }
    }, [createStatus, updateStatus, navigator]);

    const handleFormChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Create a mutable copy of Status
    const updatedStatus = {...Status};
    delete updatedStatus.Archived;

    //Create a mutable copy for discount type
    const updatedDiscountType = {...DiscountType}
    delete updatedDiscountType.FixedAmount

    const handleProductChange = (index, field, value) => {
        setProductWithDiscount(prev => {
            const updated = [...prev];
            updated[index] = {...updated[index], [field]: value};
            return updated;
        });
    };

    const validateForm = (formData) => {
        const newErrors = {
            discountType: false,
            discount: false,
        };

        if (formData.isAllProducts) {
            if (!formData.discountType) newErrors.discountType = true;
            if (!formData.discount) newErrors.discount = true;
        }

        setErrors(newErrors);

        // Return true if there are no errors
        return !Object.values(newErrors).includes(true);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const basePayload = {
            id: userRole.scopeId,
            customer: params.customerId,
            isAllProducts: formData.isAllProducts,
            maximumDiscount: formData.maximumDiscount,
            products: formData.isAllProducts ? [] : productWithDiscount,
            startDate: formData.startDate,
            endDate: formData.endDate,
            status: formData.status,
        };

        // Conditionally include discount fields only if isAllProducts is true
        if (formData.isAllProducts) {
            basePayload.discountType = formData.discountType;
            basePayload.discount = formData.discount;
        }

        if (!validateForm(basePayload)) {
            return; // Stop submission if validation fails
        }

        if (customerDiscountEdit) {
            delete basePayload.id
            delete basePayload.customer
            dispatch(updateCustomerWithDiscount({...userRole, discountId: params.discountId, data: basePayload}))
        } else {
            dispatch(createDiscountToCustomer({...userRole, data: basePayload}))
        }
    };


    return (
        <>
            <SubHeader title={params.discountId ? "Update discount" : "Add Discount"}>
                {customerDiscountEdit ?
                    <Button to={`/customer/${params.customerId}`}
                            className="btn-sm bg-primary bg-opacity-50">Back</Button> :
                    <Button to="../" className="btn-sm bg-primary bg-opacity-50">Back</Button>}
            </SubHeader>
            <form onSubmit={handleSubmit}>
                {error && (
                    <div className="invalid-feedback d-block mb-2">
                        {error}
                    </div>
                )}
                <div className="d-xl-flex justify-content-between align-items-center mb-5 gap-3">
                    <div className="d-flex gap-3">
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="isAllProducts"
                                checked={formData.isAllProducts}
                                onChange={(e) => handleFormChange("isAllProducts", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="isAllProducts">
                                All Products
                            </label>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 col-xl-3">
                            {/* Maximum discount */}
                            <div>
                                <label className="form-label mb-0 fs-md">Maximum discount</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={formData.maximumDiscount}
                                    onChange={(e) => handleFormChange("maximumDiscount", Number(e.target.value))}
                                />
                            </div>
                        </div>

                        <div className="col-md-6 col-xl-3">
                            {/* Status dropdown */}
                            <div>
                                <label className="form-label mb-0 fs-md">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    name="status"
                                    value={formData.status}
                                    onChange={(e) => handleFormChange("status", e.target.value)}
                                >
                                    <option value="" disabled>--Select payment type</option>
                                    {
                                        Object.keys(updatedStatus).map((key) => {
                                            const value = updatedStatus[key];
                                            return <option key={key} value={value}>{camelToTitle(key)}</option>
                                        })
                                    }
                                </select>
                            </div>
                        </div>

                        <div className="col-md-6 col-xl-3">
                            {/* Start Date */}
                            <div>
                                <label className="form-label mb-0 fs-md">Start Date</label>
                                <input
                                    className="form-control form-control-sm"
                                    name="startDate"
                                    type="date"
                                    value={formData.startDate ? formData.startDate.split("T")[0] : ""}
                                    onChange={(e) => handleFormChange("startDate", e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-md-6 col-xl-3">
                            {/* End Date */}
                            <div>
                                <label className="form-label mb-0 fs-md">End Date</label>
                                <input
                                    className="form-control form-control-sm"
                                    name="endDate"
                                    type="date"
                                    value={formData.endDate ? formData.endDate.split("T")[0] : ""}
                                    onChange={(e) => handleFormChange("endDate", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                </div>
                {formData.isAllProducts && (
                    <div className="row card-sm">
                        <div className="col-lg-3">
                            <label className="form-label mb-1">Discount Type</label>
                            <select
                                className={`form-select mb-2 ${errors.discountType ? "is-invalid" : ""}`}
                                value={formData.discountType}
                                onChange={(e) => {
                                    handleFormChange("discountType", e.target.value);
                                    if (e.target.value) {
                                        setErrors((prevErrors) => ({...prevErrors, discountType: ""}));
                                    }
                                }}
                            >
                                <option value="" disabled>
                                    --Select discount type
                                </option>
                                {Object.keys(updatedDiscountType).map((key) => (
                                    <option key={key} value={updatedDiscountType[key]}>
                                        {camelToTitle(key)}
                                    </option>
                                ))}
                            </select>
                            {errors.discountType && (
                                <div className="invalid-feedback">
                                    Please select a service type
                                </div>
                            )}
                        </div>
                        <div className="col-lg-3">
                            <label className="form-label mb-1">Discount</label>
                            <input
                                type="text"
                                className={`form-control ${errors.discount ? "is-invalid" : ""}`}
                                value={formData.discount}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    handleFormChange("discount", value);

                                    if (value) {
                                        setErrors((prevErrors) => ({...prevErrors, discount: ""}));
                                    }
                                }}
                            />
                            {errors.discount && (
                                <div className="invalid-feedback">
                                    Please enter percentage
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {(!formData.isAllProducts) && (
                    productWithDiscount.map((product, index) => (
                        <div key={index} className="row mb-3 mt-3 row-gap-2 card-sm">
                            <div className="col-lg-3">
                                {/* Product Select */}
                                <SelectOption
                                    className="form-select"
                                    value={product.productId}
                                    options={products}
                                    isSearch={true}
                                    onChange={(e) => handleProductChange(index, "productId", e.target.value)}
                                />
                            </div>

                            <div className="col-lg-3">
                                {/* Discount Type Select */}
                                <SelectOption
                                    className="form-select"
                                    value={product.productDiscountType}
                                    options={DiscountType}
                                    enums={true}
                                    onChange={(e) =>
                                        handleProductChange(index, "productDiscountType", e.target.value)
                                    }
                                />
                            </div>

                            <div className="col-lg-3">
                                {/* Discount Amount Input */}
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={product.productDiscountType === "Percentage" ? "%" : "Rs"}
                                        value={product.discountAmount}
                                        onChange={(e) =>
                                            handleProductChange(index, "discountAmount", Number(e.target.value))
                                        }
                                    />
                                    <span
                                        className="input-group-text">{product.productDiscountType === "Percentage" ? "%" : "Rs"}</span>
                                </div>
                            </div>

                            <div className="col-lg-3">
                                {/* Add/Remove Buttons */}
                                <button className="btn btn-outline-success me-2" type="button" onClick={handleAddRow}>
                                    +
                                </button>
                                {productWithDiscount.length > 1 && (
                                    <button
                                        className="btn btn-outline-danger"
                                        type="button"
                                        onClick={() => handleRemoveRow(index)}
                                    >
                                        −
                                    </button>
                                )}
                            </div>

                        </div>
                    ))
                )}
                <button
                    type="submit"
                    className="btn btn-primary mt-3"
                    disabled={createStatus === 'loading' || updateStatus === 'loading'}
                >
                    Submit
                </button>
            </form>
        </>
    );
};

export default DiscountAdd;